<template>
  <WizardModalModern
    v-if="isVisible"
    :machine="wizardMachine"
    :show-modal="isVisible"
    :modal-title="modalTitle"
    size="large"
    @close="handleClose"
    @cancel="handleCancel"
  ></WizardModalModern>
</template>

<script setup lang="ts">
import { ref, computed, provide, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModalModern from '@/ui/WizardModalModern.vue';
import { usePaymentRulesWizardMachine } from '@/composables/usePaymentRulesWizardMachine';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

interface PaymentRuleEntity {
  id?: number | string;
  rule_type?: string;
  amount?: number | string;
  meta?: string | null;
  [key: string]: unknown;
}

interface ModalOpenPayload {
  rule?: PaymentRuleEntity | null;
  existingRules?: PaymentRuleEntity[];
}

const { t } = useI18n();

// =============================================================================
// STATE MACHINE - Single Source of Truth
// =============================================================================

const wizardMachine = usePaymentRulesWizardMachine();

// Provide machine to all child components via inject('wizardMachine')
provide('wizardMachine', wizardMachine);

// =============================================================================
// MODAL STATE
// =============================================================================

const isVisible = ref<boolean>(false);
const rule = ref<PaymentRuleEntity | null>(null);
const existingRules = ref<PaymentRuleEntity[]>([]);

const isEditMode = computed<boolean>(() => !!rule.value);

const modalTitle = computed<string>(() => {
  return rule.value ? t('Visualizar regra de pagamento') : t('Criar regra de pagamento');
});

// Computed to pass to wizard machine for disabling edit mode
const isReadonlyMode = computed<boolean>(() => isEditMode.value);

const handleClose = (): void => {
  isVisible.value = false;
  rule.value = null;
  existingRules.value = [];
  wizardMachine.reset();
};

const handleCancel = (): void => {
  isVisible.value = false;
  rule.value = null;
  existingRules.value = [];
};

// EventBus handlers
const handleOpen = (data: ModalOpenPayload | undefined): void => {
  log('PaymentRulesModal', 'handleOpen', 'Opening modal via eventBus', {
    rule: data?.rule,
    existingRules: data?.existingRules
  });

  rule.value = data?.rule || null;
  existingRules.value = data?.existingRules || [];

  log('PaymentRulesModal', 'handleOpen', 'State after assignment', {
    rule: rule.value,
    existingRulesCount: existingRules.value.length,
    isEditMode: isEditMode.value
  });

  // Start wizard with data
  wizardMachine.start({
    rule: data?.rule,
    existingRules: data?.existingRules,
    isReadonlyMode: false // Always allow editing, readonly check is done by wizard machine based on rule existence
  });

  isVisible.value = true;
};

// Lifecycle
onMounted(() => {
  log('PaymentRulesModal', 'onMounted', 'Modal mounted, listening for events');
  eventBus.on('modal.payment-rules.open', handleOpen);
});

onBeforeUnmount(() => {
  log('PaymentRulesModal', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('modal.payment-rules.open', handleOpen);
});
</script>
